<script setup lang="ts">
  import { getConsoleUrl } from '@bv/utils/url/console/get-url';
  import { computed, onMounted, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { walletApi } from '@/api/cloud-lines/wallet';
  import type { Wallet } from '@/types/api/cloud-lines/wallet';

  const { t } = useI18n();

  const wallet = ref<Wallet>();
  const failed = ref(false);

  const rechargeUrl = getConsoleUrl('subscriptions/cloud-lines?modal=recharge-balance');

  const balance = computed<number | null>(() =>
    wallet.value?.balance !== undefined ? Number(wallet.value.balance) : null,
  );

  const balanceColor = computed(() => {
    if (balance.value === null) {
      return 'text-gray-900';
    }
    if (balance.value < 100) {
      return 'text-red-600';
    }
    if (balance.value < 200) {
      return 'text-orange-600';
    }
    return 'text-gray-900';
  });

  onMounted(async () => {
    try {
      wallet.value = await walletApi.getBalance({
        headers: {
          'x-silent': 'true',
        },
      });
    } catch {
      failed.value = true;
    }
  });
</script>

<template>
  <div class="flex h-full flex-col justify-between gap-2">
    <div class="flex items-center justify-between gap-2">
      <div class="flex items-center gap-1">
        <bv-icon
          icon="bvi-sim"
          size="md"
        />
        <span class="text-base font-medium text-gray-900">{{ t('dashboard.balance') }}</span>
      </div>

      <div
        v-if="!failed"
        class="flex items-center gap-1 font-medium"
        :class="balanceColor"
      >
        <span>{{ balance ?? '----' }}</span>
        <bv-icon
          v-if="!wallet || wallet.currency === 'SAR'"
          icon="bvi-currency-riyal"
        />
        <template v-else>{{ wallet.currency }}</template>
      </div>
    </div>

    <bv-btn
      class="w-full"
      color="gray-800"
      :href="rechargeUrl"
      size="sm"
      target="_blank"
      :text="t('dashboard.recharge_balance')"
      variant="secondary"
    />
  </div>
</template>
